import { cn } from "@/lib/cn";
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStatus,
} from "@floating-ui/react";
import type { ReactNode } from "react";

type Placement =
  | "top"
  | "top-start"
  | "top-end"
  | "right"
  | "right-start"
  | "right-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end";

export const Floating = ({
  children,
  trigger,
  placement,
  customOffset,
  customTransform,
  position,
  disableAutoUpdate,
  isOpen,
  setIsOpen,
  overwriteIndex,
  xTransform,
  closeOnScroll,
  flipOptions,
  isPortal = false,
  className = "",
}: {
  children: ReactNode | ReactNode[];
  trigger: ReactNode;
  placement?: Placement;
  customOffset?: number;
  position?: "absolute" | "fixed";
  customTransform?: number;
  xTransform?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  disableAutoUpdate?: boolean;
  overwriteIndex?: number;
  isPortal?: boolean;
  flipOptions?: Record<string, unknown>;
  closeOnScroll?: boolean;
  className?: string;
}) => {
  "use no memo";
  const flipSettings = flipOptions || {};
  const options: Record<string, any> = {
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: position || "fixed",
    transform: true,
    placement: placement ?? "bottom-end",
    middleware: [
      offset(customOffset || 4),
      flip(flipSettings),
      shift({ padding: (customTransform || 0) + 5 }),
    ],
  };
  if (!disableAutoUpdate) options.whileElementsMounted = autoUpdate;
  const { refs, floatingStyles, context, y, x } = useFloating(options);

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, {
    outsidePressEvent: "mousedown",
    ancestorScroll: !!closeOnScroll,
  });
  const { isMounted, status } = useTransitionStatus(context);
  const { getFloatingProps, getReferenceProps } = useInteractions([
    click,
    role,
    dismiss,
  ]);

  return (
    <div className="floating-WsA9Pt7X1Xr2kTA">
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="WsA9Pt7X1Xr2kTA-trigger"
      >
        {trigger}
      </div>

      {isMounted && (
        <Portal isPortal={isPortal}>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: overwriteIndex || 9999,
              transform: `translate(${x + (xTransform || 0)}px, ${y + (customTransform || 0)}px)`,
            }}
            {...getFloatingProps()}
            className={cn(
              "WsA9Pt7X1Xr2kTA-content tw-transition-opacity tw-duration-200",
              {
                "tw-opacity-0": status !== "open",
                "tw-opacity-100": status === "open",
                [className]: !!className,
              },
            )}
          >
            {children}
          </div>
        </Portal>
      )}
    </div>
  );
};

const Portal = ({
  children,
  isPortal,
}: {
  children: ReactNode;
  isPortal: boolean;
}) =>
  isPortal ? <FloatingPortal>{children}</FloatingPortal> : <>{children}</>;
